import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type {
  SkillDataTableItem,
  SkillListStateFilter,
} from '@kirin/api-client/contracts/skills';
import { SKILL_FILTER_OPTION_FIELDS } from '@kirin/api-client/contracts/skills';
import { NameCell, ProjectsCell, UsersCell } from './CellRenderers';
import { SkillRiskCell } from './SkillRiskCell';
import { SkillStateCell } from './SkillStateCell';
import { SKILL_FILTER_URL_KEYS } from './constants';
import { fetchSkillFilterOptions, getSkillFilterOptionsQueryKey } from './api';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const IncludeOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

/** What a row needs from the tab to render its Actions cell. */
export interface SkillColumnHandlers {
  onAllow: (row: SkillDataTableItem) => void;
  onRequestMark: (row: SkillDataTableItem) => void;
  isPending: (row: SkillDataTableItem) => boolean;
  /** False for a support user: they can read the tab but not write rules. */
  canEdit: boolean;
  /**
   * Shown on the disabled switch. Absent while the write gate is still
   * loading, so the tooltip does not claim a support restriction early.
   */
  readOnlyMessage?: string;
  /** Opens the Projects drilldown for the row. */
  onProjectsClick: (row: SkillDataTableItem) => void;
  /** Opens the Users drilldown for the row. */
  onUsersClick: (row: SkillDataTableItem) => void;
  /** Scope for the filter dropdowns. */
  organizationId: string;
  /** The table's pill; the dropdowns offer only values it shows. */
  listState: SkillListStateFilter;
}

type SkillColumn = ColumnConfig<SkillDataTableItem>;
/** The filterable arm of the union — the only one carrying `filterOptions`. */
type FilterableSkillColumn = Extract<SkillColumn, { filterable: true }>;

/**
 * Options for one server-backed dropdown. Every filterable column here is
 * DYNAMIC: skill names, repositories and users are all open-ended, so no
 * static list could enumerate them.
 */
function dynamicOptions(
  organizationId: string,
  listState: SkillListStateFilter,
  field: (typeof SKILL_FILTER_OPTION_FIELDS)[keyof typeof SKILL_FILTER_OPTION_FIELDS]
): FilterableSkillColumn['filterOptions'] {
  const extra = { listState };
  return {
    type: FilterOptionTypes.DYNAMIC,
    pagination: { mode: 'offset', limit: 10 },
    getQueryKey: params =>
      getSkillFilterOptionsQueryKey(organizationId, field, extra, params),
    queryFn: params =>
      fetchSkillFilterOptions(organizationId, field, extra, params),
    queryConfig: { staleTime: 2 * 60 * 1000 },
  };
}

/**
 * The five columns KIR-4699 specifies: Name, Projects, Users, Risk, Actions.
 *
 * Risk is deliberately not filterable: `riskLevel` is hard-coded null until
 * AgentMesh lands in Phase 2, so the control would filter on a constant.
 */
export function createSkillsColumns(
  handlers: SkillColumnHandlers
): ColumnConfig<SkillDataTableItem>[] {
  const organizationId = handlers.organizationId;

  return [
    {
      id: 'name',
      header: 'Name',
      accessor: 'displayName',
      width: 360,
      minWidth: 240,
      renderCell: ({ row }) => <NameCell row={row} />,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      // No `EmptyOperators`: the list requires a non-null `skill_name`, so
      // Is Empty could only ever return nothing (KIR-4850 review).
      filterOperators: [
        ...IncludeOperators,
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
      ],
      statePersistence: { urlKey: SKILL_FILTER_URL_KEYS.name },
      filterOptions: dynamicOptions(
        organizationId,
        handlers.listState,
        SKILL_FILTER_OPTION_FIELDS.NAME
      ),
    },
    {
      id: 'projects',
      header: 'Projects',
      accessor: 'projects',
      width: 110,
      renderCell: ({ row }) => (
        <ProjectsCell row={row} onClick={() => handlers.onProjectsClick(row)} />
      ),
      sortable: true,
      sortField: 'projects',
      sortKey: 'p',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [...IncludeOperators, ...EmptyOperators],
      statePersistence: { urlKey: SKILL_FILTER_URL_KEYS.projects },
      filterOptions: dynamicOptions(
        organizationId,
        handlers.listState,
        SKILL_FILTER_OPTION_FIELDS.PROJECTS
      ),
    },
    {
      id: 'users',
      header: 'Users',
      accessor: 'users',
      width: 110,
      renderCell: ({ row }) => (
        <UsersCell row={row} onClick={() => handlers.onUsersClick(row)} />
      ),
      sortable: true,
      sortField: 'users',
      sortKey: 'u',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [...IncludeOperators, ...EmptyOperators],
      statePersistence: { urlKey: SKILL_FILTER_URL_KEYS.users },
      filterOptions: dynamicOptions(
        organizationId,
        handlers.listState,
        SKILL_FILTER_OPTION_FIELDS.USERS
      ),
    },
    {
      id: 'risk',
      header: 'Risk',
      accessor: 'riskLevel',
      width: 140,
      // Nothing populates riskLevel until AgentMesh lands, so sorting would
      // reorder nothing and only suggest the column carries data.
      renderCell: ({ row }) => <SkillRiskCell riskLevel={row.riskLevel} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'listState',
      width: 200,
      minWidth: 180,
      clipOverflow: false,
      renderCell: ({ row }) => (
        <SkillStateCell
          marked={row.listState === 'marked_for_delete'}
          name={row.displayName}
          onAllow={() => handlers.onAllow(row)}
          onRequestMark={() => handlers.onRequestMark(row)}
          pending={handlers.isPending(row)}
          disabled={!handlers.canEdit}
          readOnlyMessage={handlers.readOnlyMessage}
        />
      ),
      sortable: true,
      sortField: 'listState',
      sortKey: 's',
    },
  ];
}
